import {useQueryParams} from 'presentation/pages';
import {UseOdontoNearestDateProfessionalList} from './types';
import {textHandle} from 'presentation/utils/textHandle';
import {useGetOdontoMultiAvailableTimesQuery} from '../../../../queries/useGetOdontoMultiAvailableTimesQuery';
import {isUnprocessableError} from 'presentation/utils/isUnprocessableError';
import {useMemo} from 'react';

export function useOdontoNearestDateProfessionalList({
	availableDates,
}: UseOdontoNearestDateProfessionalList) {
	const {
		SEARCH_PARAMS: {query},
	} = useQueryParams();

	const normalizedQuery = textHandle(query).sanitizeText();
	const queryToFilter = normalizedQuery === 'clear' ? '' : normalizedQuery;

	const {
		data: odontoMultiAvailableTimes,
		isFetching: isFetchingOdontoMultiAvailableTimes,
		error: errorOdontoMultiAvailableTimes,
		refetch: odontoMultiAvailableTimesRefetch,
	} = useGetOdontoMultiAvailableTimesQuery({
		dates: availableDates,
		enabled: true,
	});

	const notFoundOdontoMultiAvailableTimesError = isUnprocessableError(
		errorOdontoMultiAvailableTimes,
	);

	const odontoMultiAvailableTimesFiltered = useMemo(() => {
		if (!queryToFilter) return odontoMultiAvailableTimes;

		return odontoMultiAvailableTimes?.map(availableTimes => ({
			...availableTimes,
			times: availableTimes.times.filter(({professionalName}) =>
				textHandle(professionalName).sanitizeText().includes(queryToFilter),
			),
		}));
	}, [odontoMultiAvailableTimes, queryToFilter]);

	const odontoMultiAvailableTimesHasNoAvailableTimes = odontoMultiAvailableTimesFiltered?.every(
		list => list.times.length === 0,
	);

	const isLoading = isFetchingOdontoMultiAvailableTimes;
	const isError = !!errorOdontoMultiAvailableTimes && !notFoundOdontoMultiAvailableTimesError;
	const showNotFoundState = Boolean(
		notFoundOdontoMultiAvailableTimesError || odontoMultiAvailableTimesHasNoAvailableTimes,
	);

	return {
		isLoading,
		isError,
		odontoMultiAvailableTimesRefetch,
		showNotFoundState,
		odontoMultiAvailableTimesFiltered,
		query,
	};
}
